import type { GameContext } from '../core/context';
import type { GameEvents } from '../core/events';
import { distance, forward, type V2 } from '../core/math';
import { touchUi } from '../input/touch';
import { MISSION_REASONS } from '../player/controlLock';
import type { PointOfInterest } from '../world/types';
import type { SpawnOptions, Vehicle } from '../vehicles/types';
import type { ObjectiveInfo } from '../ui/uiTypes';
import { medalFor, replayLines, type MedalTier } from './medals';
import type { HoldStage, Line, MissionDef, RewardLine, StageDef, TargetRef } from './types';

/**
 * Runs one mission at a time: intro beat -> stages -> outro, with fail conditions, a mission timer,
 * checkpoints and retry. Everything time-based uses fixed simulation steps (deterministic).
 */

export type MissionStatus = 'idle' | 'intro' | 'running' | 'outro' | 'failed';

/** Scripting API handed to mission definitions. */
export interface MissionScript {
  readonly ctx: GameContext;
  readonly def: MissionDef;
  /** Free per-run storage (survives checkpoint retries, reset on restart). */
  readonly vars: Record<string, number | string | boolean>;
  /** Seconds since the current stage began. */
  readonly stageTime: number;
  vehicle(key: string): Vehicle | undefined;
  /** Spawn (or replace) a mission vehicle at the kerb nearest to `near`. */
  spawnVehicle(key: string, spec: string, near: TargetRef, opts?: SpawnOptions): Vehicle;
  /** Drop a mission vehicle from the job (it stays in the world). */
  releaseVehicle(key: string): void;
  /** Make a vehicle already in the world a mission vehicle (cars with a job in them: the car you got into). */
  adoptVehicle(key: string, v: Vehicle): void;
  resolve(t: TargetRef): V2;
  poi(id: string): PointOfInterest;
  say(lines: Line[]): void;
  setHeat(level: number): void;
  heat(): number;
  /**
   * Dispatch a patrol near a point (tip-offs). With `minDistance` the car is put on the road nearest
   * `near` that is at least that far from the player (preferring spots out of their line of sight),
   * so it drives in rather than appearing on top of them.
   */
  callPolice(near: TargetRef, opts?: { minDistance?: number }): void;
  cinematic(at: TargetRef, seconds: number, distance?: number): void;
  /**
   * A short camera beat on `at` (an arrival, a reveal) that never steals the picture during danger: it
   * only plays at heat 0 while the player is on foot or as good as stopped. Control stays with the player.
   */
  glance(at: TargetRef, seconds?: number): void;
  toast(text: string, kind?: 'info' | 'good' | 'bad' | 'warn'): void;
  startTimer(seconds: number, reason: string): void;
  stopTimer(): void;
  readonly timeLeft: number | null;
  /** Checkpoint setup: put the player at `at` (on foot) or behind the wheel of a mission vehicle. */
  placePlayer(at: TargetRef, inVehicle?: string): void;
  /**
   * Listen to a game event while the current stage runs ('stage', default) or for the rest of this
   * attempt ('attempt': until the job is done, fails or is abandoned). Unsubscribed automatically.
   */
  listen<K extends keyof GameEvents>(name: K, fn: (payload: GameEvents[K]) => void, scope?: 'stage' | 'attempt'): void;
  /** Hand the player a weapon (and select it). Returns false without a weapons module. */
  giveWeapon(id: string, ammo?: number): boolean;
  /** Fail the job now (rules such as "no shooters"). */
  fail(reason: string): void;
  /** Complete the running stage now (another way through it, e.g. shooting a lock instead of jemmying it). */
  advance(): void;
  /** Id of the stage that is running. */
  readonly stageId: string | null;
  /**
   * A replay from the book (docs/MISSIONS.md §Replays): pays the bonus only, grants no unlocks. Jobs keep
   * one-off gifts to themselves on a replay (Sid's free rounds).
   */
  readonly replay: boolean;
}

export interface MissionHud {
  objective: ObjectiveInfo | null;
  /** Current target (minimap/beacon) or null. */
  target: V2 | null;
  /** Mission vehicles to show while the player is not in them. */
  vehicleMarkers: V2[];
  hold: { available: boolean; prompt: string } | null;
}

/** How a finished run went (stamps, best times: src/missions/medals.ts). */
export interface RunSummary {
  replay: boolean;
  /** The stamp earned (0 for jobs without stamps). */
  medal: MedalTier;
  /** Seconds from the end of the intro to the last objective, checkpoint retries included. */
  time: number;
}

export interface RunnerCallbacks {
  /** Intro/outro presentation + results; the system wires these to UI/camera. */
  onStarted(def: MissionDef): void;
  onCompleted(def: MissionDef, lines: RewardLine[], total: number, run?: RunSummary): void;
  onFailed(def: MissionDef, reason: string, canRetry: boolean, silent: boolean): void;
}

/** Out of the abandon vehicle and further than this fraction of `abandon.distance`: warn. */
export const ABANDON_WARN_FRACTION = 0.6;
/** Seconds past `abandon.distance` before the job fails (cancelled by getting back in / coming back). */
export const ABANDON_GRACE = 10;
/** A `glance` camera beat, by default. */
export const GLANCE_SECONDS = 2.6;
/**
 * The outro lasts as long as its lines (never more than this), so the payoff isn't cut off by the tally.
 * Three full lines of Mags (the Gullet Job, Last Orders) run to about 18 s; at 14 s the last one — the
 * line the arc hangs on — still lost its tail under the JOB DONE sheet.
 */
export const OUTRO_MAX = 20;
/** After this long into the outro, E / the interact button skips to the tally (like E through the intro). */
export const OUTRO_SKIP_AFTER = 2;

/** How long a subtitle stays up: by its length, between 2.4 and 6 s, unless the line says. */
export const lineSeconds = (l: Line): number => l.seconds ?? Math.min(6, Math.max(2.4, 1.4 + l.text.length * 0.05));
/** Seconds a run of lines takes to play (each one, plus the gap after it). */
export const linesSeconds = (lines: readonly Line[]): number => lines.reduce((s, l) => s + lineSeconds(l) + 0.25, 0);

export class MissionRunner {
  status: MissionStatus = 'idle';
  def: MissionDef | null = null;
  stageIndex = 0;
  stageTime = 0;
  missionTime = 0;
  holdProgress = 0;
  checkpoint = -1;
  blocked = false;
  /** This run is a replay from the book (bonus only, no unlocks). */
  replay = false;
  /** Seconds spent with the job running (not the intro / outro / a result sheet): the run's time. */
  runTime = 0;
  /** Highest wanted level seen while the job ran (stamps: "never above level 1"). */
  maxHeat = 0;
  /** Pending dialogue with the sim time it should show. */
  private queue: { at: number; line: Line }[] = [];
  private vehicles = new Map<string, number>();
  /** Mission vehicles the player has been behind the wheel of during this attempt. */
  private driven = new Set<string>();
  private vars: Record<string, number | string | boolean> = {};
  private timer: { left: number; reason: string } | null = null;
  /** Abandon warning: metres from the vehicle while warned (null = not warned). */
  private abandonAway: number | null = null;
  /** Grace seconds left once past the abandon distance (null = not counting down). */
  private abandonLeft: number | null = null;
  private introLeft = 0;
  private outroLeft = 0;
  private outroTime = 0;
  private holdAvailable = false;
  /** Event subscriptions made by the job's scripts: per stage, and per attempt. */
  private stageSubs: (() => void)[] = [];
  private attemptSubs: (() => void)[] = [];
  /** The scripting API (also read by the system for result lines). */
  readonly script: MissionScript;

  constructor(
    private readonly ctx: GameContext,
    private readonly cb: RunnerCallbacks,
  ) {
    const self = this;
    this.script = {
      ctx,
      get def() {
        return self.def!;
      },
      get vars() {
        return self.vars;
      },
      get stageTime() {
        return self.stageTime;
      },
      get stageId() {
        return self.stage?.id ?? null;
      },
      get replay() {
        return self.replay;
      },
      vehicle: (key) => this.vehicle(key),
      spawnVehicle: (key, spec, near, opts) => this.spawnVehicle(key, spec, near, opts),
      releaseVehicle: (key) => {
        const v = this.vehicle(key);
        v?.tags.delete('mission');
        this.vehicles.delete(key);
      },
      adoptVehicle: (key, v) => {
        v.tags.add('mission');
        v.tags.add(`mission:${key}`);
        this.vehicles.set(key, v.id);
      },
      resolve: (t) => this.resolve(t),
      poi: (id) => this.poi(id),
      say: (lines) => this.say(lines),
      setHeat: (level) => {
        const heat = ctx.services.tryGet('heat');
        if (heat) heat.setLevel(level);
        else ctx.hooks.call('setHeat', level);
      },
      heat: () => ctx.state.heat,
      callPolice: (near, opts) => {
        const p = this.resolve(near);
        const min = opts?.minDistance ?? 0;
        const police = ctx.services.tryGet('police');
        if (!police) return;
        if (min <= 0) {
          police.spawnPatrol(p);
          return;
        }
        // Best spot first; a passing car may be sitting on it, so fall back to the next few.
        for (const spot of this.patrolSpots(p, min)) if (police.spawnPatrol(spot) > 0) return;
      },
      cinematic: (at, seconds, dist) => {
        const p = this.resolve(at);
        ctx.services.tryGet('cameraRig')?.cinematic(p, seconds, dist !== undefined ? { distance: dist } : {});
      },
      glance: (at, seconds = GLANCE_SECONDS) => {
        const player = ctx.services.get('player');
        const speed = player.vehicle?.speed ?? 0;
        if (ctx.state.heat > 0 || (player.mode !== 'foot' && speed > 2)) return;
        this.script.cinematic(at, seconds, 45);
      },
      toast: (text, kind = 'info') => ctx.services.tryGet('ui')?.toast(text, kind),
      startTimer: (seconds, reason) => {
        this.timer = { left: seconds, reason };
      },
      stopTimer: () => {
        this.timer = null;
      },
      get timeLeft() {
        return self.timer ? self.timer.left : null;
      },
      placePlayer: (at, inVehicle) => {
        const player = ctx.services.get('player');
        if (inVehicle) {
          const v = this.vehicle(inVehicle);
          if (v) {
            if (player.vehicle !== v) player.enterVehicle(v, true);
            return;
          }
        }
        if (player.mode === 'driving') player.exitVehicle();
        const p = this.resolve(at);
        player.teleport(p);
      },
      listen: (name, fn, scope = 'stage') => {
        const off = ctx.events.on(name, fn);
        (scope === 'attempt' ? this.attemptSubs : this.stageSubs).push(off);
      },
      giveWeapon: (id, ammo) => {
        const w = ctx.services.tryGet('weapons');
        if (!w) return false;
        return w.give(id as Parameters<typeof w.give>[0], ammo !== undefined ? { ammo, select: true } : { select: true });
      },
      fail: (reason) => this.fail(reason),
      advance: () => {
        if (this.status === 'running') this.completeStage();
      },
    };
  }

  // ---------------------------------------------------------------- queries

  get active(): boolean {
    return this.status === 'intro' || this.status === 'running' || this.status === 'outro';
  }

  get stage(): StageDef | null {
    return this.def?.stages[this.stageIndex] ?? null;
  }

  get timeLeft(): number | null {
    return this.timer?.left ?? null;
  }

  vehicle(key: string): Vehicle | undefined {
    const id = this.vehicles.get(key);
    return id === undefined ? undefined : this.ctx.services.get('vehicles').get(id);
  }

  missionVehicleIds(): number[] {
    return [...this.vehicles.values()];
  }

  poi(id: string): PointOfInterest {
    const p = this.ctx.services.get('layout').pois.find((x) => x.id === id);
    if (!p) throw new Error(`mission: unknown POI '${id}'`);
    return p;
  }

  resolve(t: TargetRef): V2 {
    if ('poi' in t) {
      const p = this.poi(t.poi);
      if (!t.offset) return p.pos;
      const f = forward(p.heading);
      return { x: p.pos.x + f.x * t.offset, z: p.pos.z + f.z * t.offset };
    }
    if ('vehicle' in t) return this.vehicle(t.vehicle)?.pos ?? { x: 0, z: 0 };
    if ('pos' in t) return t.pos;
    return t.fn(this.script);
  }

  // ---------------------------------------------------------------- lifecycle

  start(def: MissionDef, opts: { replay?: boolean } = {}): void {
    if (this.active) this.abort();
    this.unlisten(true);
    this.def = def;
    this.replay = !!opts.replay;
    this.runTime = 0;
    this.maxHeat = 0;
    this.vars = {};
    this.timer = null;
    this.queue = [];
    this.checkpoint = -1;
    this.missionTime = 0;
    this.clearAbandonWarning();
    this.releaseAllVehicles(true);
    this.ctx.state.activeMissionId = def.id;
    def.setup?.(this.script);
    this.status = 'intro';
    this.introLeft = Math.min(8, Math.max(3.5, def.intro.reduce((s, l) => s + lineSeconds(l), 0)));
    this.say(def.intro);
    if (def.introFocus) this.script.cinematic(def.introFocus, this.introLeft, 70);
    this.ctx.services.get('player').controls.acquire('mission');
    this.cb.onStarted(def);
    this.ctx.events.emit('missionStarted', { id: def.id });
    def.onAttempt?.(this.script);
    this.enterStage(0);
  }

  /** Retry from the last checkpoint (falls back to a full restart). */
  retry(): void {
    const def = this.def;
    if (!def) return;
    if (this.checkpoint < 0) return this.start(def);
    const idx = this.checkpoint;
    this.ctx.state.activeMissionId = def.id;
    this.queue = [];
    this.status = 'running';
    this.clearAbandonWarning();
    this.unlisten(true);
    def.stages[idx]!.checkpoint!.restore(this.script);
    this.ctx.services.get('player').controls.drop(...MISSION_REASONS);
    this.ctx.events.emit('missionStarted', { id: def.id });
    def.onAttempt?.(this.script);
    this.enterStage(idx);
  }

  /** Stop without presentation (new game, debug). */
  abort(): void {
    if (!this.def) return;
    this.status = 'idle';
    this.ctx.state.activeMissionId = null;
    this.queue = [];
    this.timer = null;
    this.clearAbandonWarning();
    this.unlisten(true);
    // Safety net: whatever stage it stopped in, the job's reasons go with it.
    this.ctx.services.get('player').controls.drop(...MISSION_REASONS);
    this.releaseAllVehicles(false);
  }

  /** Skip the intro or the outro if one is playing, otherwise complete the current stage. */
  skipStage(): void {
    if (this.status === 'intro') this.endIntro();
    else if (this.status === 'running') this.completeStage();
    else if (this.status === 'outro') this.finish();
  }

  /** Why the last job failed (for the HUD/tests; the event log ring buffer can roll over). */
  lastFailReason: string | null = null;

  fail(reason: string, silent = false): void {
    if (!this.active || !this.def) return;
    const def = this.def;
    this.lastFailReason = reason;
    const canRetry = this.checkpoint >= 0;
    // What was left on the clock (0 when it ran out), so a checkpoint restore can carry it over
    // instead of handing out a fresh timer on every retry.
    if (this.timer) this.vars.timeLeftAtFail = Math.max(0, this.timer.left);
    else delete this.vars.timeLeftAtFail;
    this.status = 'failed';
    this.queue = [];
    this.timer = null;
    this.clearAbandonWarning();
    this.unlisten(true);
    this.ctx.state.activeMissionId = null;
    // An arrest / knock-down (silent) keeps the controls under its own reason until the respawn.
    this.ctx.services.get('player').controls.drop(...MISSION_REASONS);
    def.onResolved?.(this.script, 'failed', reason);
    this.ctx.events.emit('missionFailed', { id: def.id, reason });
    this.cb.onFailed(def, reason, canRetry, silent);
  }

  // ---------------------------------------------------------------- per step

  fixedUpdate(dt: number): void {
    const now = this.ctx.state.time;
    while (this.queue.length && this.queue[0]!.at <= now) {
      const { line } = this.queue.shift()!;
      this.ctx.services.tryGet('ui')?.say(line.who, line.text, lineSeconds(line));
    }
    if (!this.def || !this.active) return;
    this.missionTime += dt;

    if (this.status === 'intro') {
      this.introLeft -= dt;
      if (this.introLeft <= 0 || (this.introLeft < 7 && (this.ctx.input.pressed('enterExit') || this.ctx.input.pressed('vehicle')))) this.endIntro();
      return;
    }
    if (this.status === 'outro') {
      this.outroLeft -= dt;
      this.outroTime += dt;
      if (this.outroLeft <= 0 || (this.outroTime > OUTRO_SKIP_AFTER && (this.ctx.input.pressed('enterExit') || this.ctx.input.pressed('vehicle')))) this.finish();
      return;
    }

    this.stageTime += dt;
    this.runTime += dt;
    if (this.ctx.state.heat > this.maxHeat) this.maxHeat = this.ctx.state.heat;
    if (this.timer) {
      this.timer.left -= dt;
      if (this.timer.left <= 0) return this.fail(this.timer.reason);
    }
    if (this.checkFailures(dt)) return;
    const stage = this.stage;
    if (!stage) return;
    if (stage.timeLimit !== undefined && this.stageTime > stage.timeLimit) return this.fail('Too slow: you ran out of time. Try again and go faster.');
    stage.onTick?.(this.script, dt);
    if (!this.active || this.stage !== stage) return; // script may have failed/advanced
    if (this.evaluate(stage, dt)) this.completeStage();
  }

  private checkFailures(dt: number): boolean {
    const def = this.def!;
    const player = this.ctx.services.get('player');
    for (const pr of def.protect ?? []) {
      if (!this.vehicles.has(pr.vehicle)) continue;
      const v = this.vehicle(pr.vehicle);
      if (!v || v.destroyed || v.health / v.spec.maxHealth < pr.minHealth) {
        this.fail(pr.reason);
        return true;
      }
    }
    if (def.abandon) {
      const ab = def.abandon;
      const v = this.vehicle(ab.vehicle);
      if (v && player.vehicle === v) this.driven.add(ab.vehicle);
      // Only once you've actually had the car: walking off before you reach it (or respawning across
      // town after an arrest) isn't "abandoning" it.
      const away = v && this.driven.has(ab.vehicle) && player.vehicle !== v ? distance(player.pos, v.pos) : 0;
      const label = ab.label ?? 'the car';
      if (away > ab.distance * ABANDON_WARN_FRACTION) {
        if (this.abandonAway === null) this.script.toast(`Don’t leave ${label} behind — get back to it.`, 'warn');
        this.abandonAway = away;
        // Past the limit: a short countdown, so there's still a moment to turn round (MISSIONS-8).
        if (away > ab.distance) {
          this.abandonLeft = (this.abandonLeft ?? ab.grace ?? ABANDON_GRACE) - dt;
          if (this.abandonLeft <= 0) {
            this.fail(ab.reason);
            return true;
          }
        } else this.abandonLeft = null;
      } else if (this.abandonAway !== null) {
        if (v && player.vehicle === v) this.script.toast(`Back in ${label}.`, 'good');
        this.clearAbandonWarning();
      }
    }
    return false;
  }

  /** Drop the scripts' event subscriptions: the stage's, and with `attempt` the attempt-long ones too. */
  private unlisten(attempt: boolean): void {
    for (const off of this.stageSubs) off();
    this.stageSubs = [];
    if (!attempt) return;
    for (const off of this.attemptSubs) off();
    this.attemptSubs = [];
  }

  private clearAbandonWarning(): void {
    this.abandonAway = null;
    this.abandonLeft = null;
  }

  /** True when the stage's goal is met. Also maintains `blocked` / hold availability. */
  private evaluate(stage: StageDef, dt: number): boolean {
    const player = this.ctx.services.get('player');
    this.blocked = false;
    this.holdAvailable = false;
    // "Get into the red Vesper": that car is the get-in target over a nearer parked one (docs/CONTROLS-POLISH.md).
    player.setPreferredVehicle(stage.kind === 'enterVehicle' ? (this.vehicle(stage.vehicle) ?? null) : null);
    switch (stage.kind) {
      case 'wait':
        return this.stageTime >= stage.seconds;
      case 'escapeHeat':
        return this.ctx.state.heat === 0;
      case 'enterVehicle': {
        const v = this.vehicle(stage.vehicle);
        return !!v && player.mode === 'driving' && player.vehicle === v;
      }
      case 'goTo': {
        const at = this.resolve(stage.at);
        if (distance(player.pos, at) > stage.radius) return false;
        const driving = player.mode === 'driving' ? player.vehicle : null;
        if (stage.mode === 'foot' && player.mode !== 'foot') return false;
        if (stage.mode === 'vehicle' && !driving) return false;
        if (stage.vehicle && driving !== this.vehicle(stage.vehicle)) return false;
        if (stage.vehicleSpec && driving?.spec.id !== stage.vehicleSpec) return false;
        if (stage.minHealth !== undefined && (!driving || driving.health / driving.spec.maxHealth < stage.minHealth)) {
          this.blocked = true;
          return false;
        }
        if (stage.requireHeat0 && this.ctx.state.heat > 0) {
          this.blocked = true;
          return false;
        }
        if (stage.requireUnseen && this.ctx.services.tryGet('heat')?.seen) {
          this.blocked = true;
          return false;
        }
        return true;
      }
      case 'hold':
        return this.evaluateHold(stage, dt);
      case 'custom':
        if (stage.blocked?.(this.script)) {
          this.blocked = true;
          return false;
        }
        return stage.done(this.script);
    }
  }

  private evaluateHold(stage: HoldStage, dt: number): boolean {
    const player = this.ctx.services.get('player');
    const at = this.resolve(stage.at);
    if (distance(player.pos, at) > stage.radius) return false;
    if ((stage.onFoot ?? true) && player.mode !== 'foot') return false;
    if (stage.near) {
      const v = this.vehicle(stage.near.vehicle);
      if (!v || distance(v.pos, at) > stage.near.radius) {
        this.blocked = true;
        return false;
      }
    }
    this.holdAvailable = true;
    if (this.ctx.input.held('enterExit')) {
      const before = this.holdProgress;
      this.holdProgress = Math.min(1, this.holdProgress + dt / stage.seconds);
      if (this.holdProgress !== before) stage.onProgress?.(this.script, this.holdProgress);
    }
    return this.holdProgress >= 1;
  }

  // ---------------------------------------------------------------- transitions

  private endIntro(): void {
    if (this.status !== 'intro') return;
    this.status = 'running';
    this.stageTime = 0;
    this.ctx.services.get('player').controls.release('mission');
    const rig = this.ctx.services.tryGet('cameraRig');
    if (rig?.isCinematic) rig.setOverride(null);
    const ui = this.ctx.services.tryGet('ui');
    ui?.letterbox(false);
    // Skipped (or simply over): the briefing card goes with the bars.
    ui?.dismissIntro();
  }

  private enterStage(i: number): void {
    const def = this.def!;
    if (i === 0 || (def.stages[i]?.checkpoint && this.status !== 'intro')) {
      this.driven.clear();
      this.clearAbandonWarning();
    }
    this.unlisten(false);
    this.stageIndex = i;
    this.stageTime = 0;
    this.holdProgress = 0;
    this.blocked = false;
    const stage = def.stages[i];
    if (!stage) return;
    if (stage.checkpoint && this.checkpoint !== i) {
      this.checkpoint = i;
      if (this.status === 'running') this.script.toast(`Checkpoint — ${stage.checkpoint.label}`, 'good');
    }
    stage.onEnter?.(this.script);
    if (stage.lines) this.say(stage.lines);
  }

  private completeStage(): void {
    const def = this.def!;
    const stage = this.stage;
    stage?.onComplete?.(this.script);
    if (!this.active || this.stage !== stage) return;
    const next = this.stageIndex + 1;
    if (next >= def.stages.length) return def.noOutro ? this.finish() : this.beginOutro();
    this.enterStage(next);
  }

  private beginOutro(): void {
    const def = this.def!;
    this.status = 'outro';
    if (this.timer) this.vars.timeLeftAtEnd = this.timer.left;
    this.timer = null;
    // Every line gets said before the tally comes down over it (the old 60 % cut the last line off).
    this.queue = [];
    this.outroTime = 0;
    this.outroLeft = Math.min(OUTRO_MAX, Math.max(2.5, linesSeconds(def.outro)));
    this.say(def.outro);
    // A beat like the intro: the camera goes to the payoff and the bars come down, so nobody drives
    // blind for twenty seconds while Mags talks (and E / USE means "skip", not "get out of the car").
    // The job is over and the heat is 0 by then, so nothing is taken from the player mid-danger.
    this.ctx.services.get('player').controls.acquire('mission');
    this.ctx.services.tryGet('ui')?.letterbox(true);
    if (def.outroFocus) this.script.cinematic(def.outroFocus, this.outroLeft + 0.5, 55);
  }

  private finish(): void {
    const def = this.def!;
    const full = def.payout ? def.payout(this.script) : [{ label: 'Payment', amount: def.reward }];
    const run: RunSummary = { replay: this.replay, medal: medalFor(def.id, { lines: full, vars: this.vars, time: this.runTime, maxHeat: this.maxHeat }), time: this.runTime };
    // A replay pays the bonus part only: the base fee was paid the first time (no money tap).
    const lines = this.replay ? replayLines(full) : full;
    const total = lines.reduce((s, l) => s + l.amount, 0);
    this.status = 'idle';
    // Skipped early: the rest of the lines would only play under the tally, and the camera would sit
    // on the payoff for the rest of the beat while the player is back at the wheel.
    this.queue = [];
    const rig = this.ctx.services.tryGet('cameraRig');
    if (rig?.isCinematic) rig.setOverride(null);
    this.ctx.state.activeMissionId = null;
    this.unlisten(true);
    // Paid out under an arrest / knock-down (missionSystem `blown`): the outcome's reason holds until the respawn.
    this.ctx.services.get('player').controls.drop(...MISSION_REASONS);
    const save = this.ctx.services.get('save');
    // A replay never moves the story: the unlocks were granted the first time.
    if (!this.replay) for (const u of def.unlocks ?? []) save.addUnlock(u);
    def.onResolved?.(this.script, 'completed');
    this.releaseAllVehicles(false);
    this.ctx.events.emit('missionCompleted', this.replay ? { id: def.id, reward: total, replay: true } : { id: def.id, reward: total });
    this.cb.onCompleted(def, lines, total, run);
  }

  // ---------------------------------------------------------------- helpers

  say(lines: Line[]): void {
    let at = Math.max(this.ctx.state.time, this.queue.length ? this.queue[this.queue.length - 1]!.at : 0);
    for (const line of lines) {
      this.queue.push({ at, line });
      at += lineSeconds(line) + 0.25;
    }
  }

  spawnVehicle(key: string, spec: string, near: TargetRef, opts: SpawnOptions = {}): Vehicle {
    const vehicles = this.ctx.services.get('vehicles');
    const old = this.vehicle(key);
    const player = this.ctx.services.get('player');
    if (old && player.vehicle !== old) vehicles.despawn(old);
    const want = this.resolve(near);
    // Kerbside spot nearest the target, so mission cars sit where traffic expects parked cars.
    let best: { pos: V2; heading: number } | null = null;
    let bestD = 45;
    for (const s of this.ctx.services.get('layout').parkedSpots) {
      const d = distance(s.pos, want);
      if (d < bestD && distance(s.pos, player.pos) > 3) {
        bestD = d;
        best = s;
      }
    }
    const spot = best ?? { pos: want, heading: opts.heading ?? 0 };
    for (const v of vehicles.query(spot.pos, 4.5)) if (v !== player.vehicle && !this.missionVehicleIds().includes(v.id)) vehicles.despawn(v);
    const v = vehicles.spawn(spec, spot.pos, { heading: spot.heading, ...opts, tags: ['mission', `mission:${key}`, ...(opts.tags ?? [])] });
    this.vehicles.set(key, v.id);
    return v;
  }

  /**
   * A point on a lane near `near` but at least `min` m (and at most `min` + 60 m) from the player,
   * preferring spots the player can't see (round a corner, the far end of a yard). Falls back to `near`.
   */
  patrolSpot(near: V2, min: number): V2 {
    return this.patrolSpots(near, min)[0]!;
  }

  /** `patrolSpot`'s candidates, best first, at least 8 m apart (up to `count`; `near` when there are none). */
  patrolSpots(near: V2, min: number, count = 5): V2[] {
    const net = this.ctx.services.tryGet('traffic')?.roadNet;
    if (!net) return [near];
    const me = this.ctx.services.get('player').pos;
    const cands: { pt: V2; score: number }[] = [];
    for (const l of net.lanes) {
      // A patrol car can't be laid on in an alley (no room beside the walls): streets only.
      if (l.alley) continue;
      for (let s = 0; s <= l.length; s += 6) {
        const pt = { x: l.a.x + l.dir.x * s, z: l.a.z + l.dir.z * s };
        const d = distance(pt, me);
        if (d < min || d > min + 60) continue;
        cands.push({ pt, score: distance(pt, near) + (d - min) * 0.3 + (this.ctx.physics.lineOfSight(me, pt) ? 40 : 0) });
      }
    }
    cands.sort((a, b) => a.score - b.score);
    const out: V2[] = [];
    for (const c of cands) {
      if (out.length >= count) break;
      if (out.every((q) => distance(q, c.pt) >= 8)) out.push(c.pt);
    }
    return out.length ? out : [near];
  }

  private releaseAllVehicles(despawn: boolean): void {
    const vehicles = this.ctx.services.get('vehicles');
    const player = this.ctx.services.get('player');
    player.setPreferredVehicle(null);
    for (const id of this.vehicles.values()) {
      const v = vehicles.get(id);
      if (!v) continue;
      if (despawn && player.vehicle !== v) vehicles.despawn(v);
      else v.tags.delete('mission');
    }
    this.vehicles.clear();
  }

  /** What the HUD should show this frame. */
  hud(): MissionHud {
    const def = this.def;
    const stage = this.stage;
    if (!def || !stage || (this.status !== 'running' && this.status !== 'intro')) {
      return { objective: null, target: null, vehicleMarkers: [], hold: null };
    }
    const player = this.ctx.services.get('player');
    let target: V2 | null = null;
    if (stage.kind === 'goTo' || stage.kind === 'hold') target = this.resolve(stage.at);
    else if (stage.kind === 'custom' && stage.at) target = this.resolve(stage.at);
    else if (stage.kind === 'enterVehicle') target = this.vehicle(stage.vehicle)?.pos ?? null;
    let text = stage.text;
    if (this.blocked) {
      if (stage.kind === 'goTo' && stage.blockedText) text = stage.blockedText;
      if (stage.kind === 'custom' && stage.blockedText) text = typeof stage.blockedText === 'function' ? stage.blockedText(this.script) : stage.blockedText;
      if (stage.kind === 'hold' && stage.near) text = stage.near.text;
    }
    const obj: ObjectiveInfo = { title: def.title.toUpperCase(), text };
    if (stage.hint) obj.hint = typeof stage.hint === 'function' ? stage.hint(touchUi.enabled) : stage.hint;
    if (target) obj.distance = distance(player.pos, target);
    const ab = def.abandon;
    if (ab && this.abandonAway !== null && this.status === 'running') {
      // Walking off from the job's car: say so loudly, and show how much rope is left.
      const label = ab.label ?? 'the car';
      obj.text = `Get back in ${label}. If you leave it behind, the job fails.`;
      delete obj.hint;
      const car = this.vehicle(ab.vehicle);
      if (car) obj.distance = distance(player.pos, car.pos);
      obj.warning =
        this.abandonLeft !== null
          ? { text: `Abandoning ${label}`, seconds: Math.max(0, this.abandonLeft) }
          : { text: `Leaving ${label} · ${Math.max(0, Math.ceil(ab.distance - this.abandonAway))} m left` };
    }
    const tl = this.timer ? this.timer.left : stage.timeLimit !== undefined ? stage.timeLimit - this.stageTime : undefined;
    if (tl !== undefined) obj.timer = tl;
    if (stage.kind === 'hold' && (this.holdProgress > 0 || this.holdAvailable)) obj.progress = this.holdProgress;
    else if (stage.kind === 'custom' && stage.progress) obj.progress = Math.max(0, Math.min(1, stage.progress(this.script)));
    else {
      // Show the condition of the protected vehicle you're responsible for right now.
      for (const pr of def.protect ?? []) {
        const v = this.vehicle(pr.vehicle);
        if (v && pr.minHealth > 0) {
          obj.progress = Math.max(0, v.health / v.spec.maxHealth);
          break;
        }
      }
    }
    const markers: V2[] = [];
    for (const id of this.vehicles.values()) {
      const v = this.ctx.services.get('vehicles').get(id);
      if (v && !v.destroyed && player.vehicle !== v) markers.push(v.pos);
    }
    return {
      objective: obj,
      target,
      vehicleMarkers: markers,
      hold: stage.kind === 'hold' ? { available: this.holdAvailable, prompt: stage.prompt } : null,
    };
  }

  snapshot(): Record<string, unknown> {
    return {
      id: this.def?.id ?? null,
      status: this.status,
      stage: this.stage?.id ?? null,
      stageIndex: this.stageIndex,
      stageKind: this.stage?.kind ?? null,
      stageTime: +this.stageTime.toFixed(2),
      holdProgress: +this.holdProgress.toFixed(2),
      blocked: this.blocked,
      checkpoint: this.checkpoint,
      lastFailReason: this.lastFailReason,
      replay: this.replay,
      runTime: +this.runTime.toFixed(1),
      maxHeat: this.maxHeat,
      timeLeft: this.timer ? +this.timer.left.toFixed(1) : null,
      abandonWarning: this.abandonAway === null ? null : { away: +this.abandonAway.toFixed(1), graceLeft: this.abandonLeft === null ? null : +this.abandonLeft.toFixed(1) },
      vehicles: Object.fromEntries(this.vehicles),
      vars: { ...this.vars },
    };
  }
}
